<template>
  <div class="planner flex min-h-0 flex-1 flex-col gap-2">
    <div v-if="store.loading" class="status status_loading px-4 py-2 text-xs text-text-muted bg-bg-surface border border-border rounded-lg" role="status">{{ messages.status.loading }}</div>
    <div v-else-if="store.error" class="status status_error px-4 py-2 text-xs text-danger bg-bg-surface border border-danger/40 rounded-lg" role="alert">{{ store.error }}</div>
    <div v-if="store.liveSyncError !== null" class="status status_sync_lost px-4 py-2 text-xs text-warning bg-bg-surface border border-warning/40 rounded-lg" role="status" :aria-label="messages.status.liveSyncLostAria">{{ messages.status.liveSyncLost(store.liveSyncError) }}</div>
    <div v-if="store.isMember && !store.isOfficer" v-show="store.viewMode !== ViewMode.Compact" class="status status_view_only px-4 py-2 text-xs text-text-muted bg-bg-surface border border-border rounded-lg" role="status">{{ messages.status.viewOnly }}</div>
    <div v-if="store.activeRaidEditLocked" class="status status_old_raid px-4 py-2 text-xs text-warning bg-bg-surface border border-warning/40 rounded-lg" role="status">{{ messages.status.oldRaidReadOnly }}</div>

    <StageChipStrip v-if="store.activeRaidId !== null" :stages="store.raidStages" :active-stage-id="store.activeStageId" :is-officer="store.isOfficer && !store.activeRaidEditLocked" :busy="store.stageSelectionPending" @select="onSelectStage" @add="onAddStage" @remove="onRemoveActiveStage" />
    <StageDiffCard v-if="store.activeRaidId !== null" :diffs="store.raidStageDiffs" :stages="store.raidStages" :active-stage-id="store.activeStageId" :is-officer="store.isOfficer && !store.activeRaidEditLocked" :selection-pending="store.stageSelectionPending" @reset="onStageReset" />

    <PlannerToolbar
      :disabled="store.loading || store.stageSelectionPending"
      :raid-published="activeRaid?.isPublished ?? null"
      :active-filter-count="activePlayerFilterCount"
      :filter-mode="boardPlayerFilterMode"
      :raids="store.raids"
      :active-raid="activeRaid"
      @open-filters="openPlayerFilters"
      @open-map="onOpenMap"
      @open-raid-audit="onOpenRaidAudit"
      @clone-raid="onCloneAnotherRaid"
      @toggle-raid-publication="onToggleRaidPublication"
      @sort-all="announce(messages.announcement.sortedByRole)"
      @reset="announce(messages.announcement.resetToSeed)"
      @select-raid="onSelectPlannedRaid"
    />

    <!--
      ISS-01 — the onboarding panel sits as a sibling of the rooms stack,
      not of the status-strip cluster. The two-layer guard (outer
      `!store.isMember`, inner loading/session gates) keeps it off the page
      for members and prevents the flash-while-loading UX bug that lived in
      the original placement. See the placement test for the exact pin.
    -->
    <GuildOnboardingPanel v-if="!store.isMember && session !== null && !store.guildsLoading && !store.loading && !store.error && store.availableGuilds.length === 0" />

    <div class="rooms flex min-h-0 flex-1 flex-col" :class="[store.viewMode === ViewMode.Compact ? 'gap-1' : 'gap-2', store.stageSelectionPending ? 'pointer-events-none opacity-60' : '']" :aria-busy="store.stageSelectionPending" :inert="store.stageSelectionPending">
      <RoomSection
        v-for="room in store.currentRooms"
        :key="room.id"
        :room="room"
        :mode="store.boardMode"
        :stage="activeStageForHeader"
        :player-filters-active="boardPlayerFiltersActive"
        :player-filter-mode="boardPlayerFilterMode"
        :matching-player-ids="matchingPlayerIds"
        @use-build="store.setActiveBuild"
        @add-build="pickerController?.openNewBuildPicker"
        @use-guild-build="store.setActiveGuildBuild"
        @add-guild-build="pickerController?.openNewGuildBuildPicker"
        @edit-guild-build="(id, index, slot) => pickerController?.openGuildBuildSlotPicker(id, index, slot)"
        @open-player-selector="openGroupPlayerSelector"
      />
    </div>

    <!--
      ISS-01 anchor — the `class="tags_filter"` wrapper preserves the original
      selector the GuildOnboardingPanel placement test
      (`src/features/gvg-planner/components/__tests__/GuildOnboardingPanel.placement.test.ts`)
      pins in the parent template. The wrapper is purely cosmetic; the strip
      component still owns its own internal layout. Keeping the class on the
      parent rather than inside the child lets the test scan `GvgPlannerView.vue`
      with a single regex without having to descend into every child SFC.

      The strip now renders below the rooms stack so the "Filter roster"
      control hangs at the bottom of the planner instead of sandwiched between
      the toolbar and the rooms. The placement test asserts that
      `<GuildOnboardingPanel>` lives between the tags-filter `</div>` and the
      rooms `<div>`; with this reordering the tags-filter strip moves below
      both, so the assertion has been retargeted to anchor on the closing
      `</div>` of the rooms stack instead.
    -->
    <div class="tags_filter">
      <TagFilterStrip :tags="store.playerTags" :active-filter="store.activeTagFilter" :is-officer="store.isOfficer" @filter-click="onTagFilterClick" />
    </div>

    <span class="sr-only" aria-live="polite" aria-atomic="true">{{ liveAnnouncement }}</span>
    <PickerController ref="pickerController" />
    <GvgMapDrawer v-model="mapDrawerOpen" @edit-group="onEditMapGroup" @edit-tag="onEditMapTag" />
    <PlayerFiltersDrawer
      v-model="playerFiltersDrawerOpen"
      :filters="drawerPlayerFilters"
      :mode="boardPlayerFilterMode"
      :target-group-id="playerSelectorGroupId"
      :target-tag-id="playerSelectorTagId"
      :pending-targets="pendingPlayerTargets"
      :pending-tag-player-ids="pendingTagPlayerIds"
      @update:filters="updateDrawerPlayerFilters"
      @update:mode="boardPlayerFilterMode = $event"
      @toggle-player="onToggleGroupPlayer"
      @toggle-tag-player="onToggleTagPlayer"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import GuildOnboardingPanel from './components/guild-admin/GuildOnboardingPanel.vue'
import GvgMapDrawer from './components/map/GvgMapDrawer.vue'
import PlayerFiltersDrawer from './components/player-filters/PlayerFiltersDrawer.vue'
import PickerController from './components/weapons/PickerController.vue'
import PlannerToolbar from './components/board/PlannerToolbar.vue'
import RoomSection from './components/board/RoomSection.vue'
import StageChipStrip from './components/calendar/StageChipStrip.vue'
import StageDiffCard from './components/calendar/StageDiffCard.vue'
import TagFilterStrip from './components/tags/TagFilterStrip.vue'
import { plannerRootEn } from './locales/en.ts'
import { plannerRootRu } from './locales/ru.ts'
import { usePlayerFilters } from './composables/usePlayerFilters'
import { useCalendarTick, watchCalendarTickForGuild } from './composables/useCalendarTick'
import { useGvgPlannerStore } from './stores/gvgPlannerStore'
import { FEATURE_ROUTE_NAMES } from './constants/routes'
import { RaidStageKind, ViewMode } from './types'
import type { RaidId, RaidStageId } from './types'

import { session } from '@/app/session'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useViewTimeouts } from '@/shared/composables'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'

interface PickerControllerExposed {
  readonly openAltPicker: (playerId: string) => void
  readonly openNewBuildPicker: (playerId: string) => void
  readonly openNewGuildBuildPicker: () => void
  readonly openGuildBuildSlotPicker: (playerId: string, buildId: string, slot: 0 | 1) => void
  readonly closePicker: () => void
  readonly isOpen: boolean
}

/**
 * Props contract for the planner view. The view is parameterless
 * today (the selected raid is read from the route param via the
 * global `beforeEach` guard in `routerGuards.ts`), but the marker
 * interface is exported so consumers that need to type-state a
 * planner-view instance share the same shape.
 */
export interface GvgPlannerViewProps {}

defineProps<GvgPlannerViewProps>()

const catalog = defineLocaleCatalog({ en: plannerRootEn, ru: plannerRootRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
const raidEditTick = useCalendarTick()
watchCalendarTickForGuild(raidEditTick, computed(() => store.activeDiscordGuildId))
watch(raidEditTick.nowTick, () => store.refreshActiveRaidEditLock(), { immediate: true })
const route = useRoute()
const router = useRouter()
const MAP_QUERY_KEY = 'map'
const MAP_QUERY_VALUE = '1'
const pickerController = ref<PickerControllerExposed | null>(null)
const mapDrawerOpen = ref(mapRouteIsOpen())
const { liveAnnouncement, scheduleTimeout, clearPendingTimeout, announce } = useViewTimeouts()
const {
  drawerOpen: playerFiltersDrawerOpen,
  boardMode: boardPlayerFilterMode,
  selectorGroupId: playerSelectorGroupId,
  selectorTagId: playerSelectorTagId,
  pendingTargets: pendingPlayerTargets,
  pendingTagPlayerIds,
  activeFilterCount: activePlayerFilterCount,
  boardFiltersActive: boardPlayerFiltersActive,
  matchingPlayerIds,
  drawerFilters: drawerPlayerFilters,
  openBoardFilters: openPlayerFilters,
  openGroupSelector: openGroupPlayerSelector,
  openTagSelector: openTagPlayerSelector,
  updateDrawerFilters: updateDrawerPlayerFilters,
  toggleGroupPlayer: onToggleGroupPlayer,
  toggleTagPlayer: onToggleTagPlayer,
} = usePlayerFilters({ announce })
const LIVE_SYNC_STALE_MS = 5 * 60 * 1000

const activeStageForHeader = computed<{ readonly id: string; readonly kind: string; readonly name: string } | null>(() => {
  const stage = store.raidStages.find((candidate) => candidate.id === store.activeStageId)
  return stage === undefined ? null : { id: stage.id, kind: stage.kind, name: stage.name }
})

const activeRaid = computed(() => store.activeRaidBoard?.raid ?? null)

watch(
  () => store.liveSyncError,
  (current) => {
    if (current === null) {
      clearPendingTimeout()
      return
    }
    scheduleTimeout(() => store.clearLiveSyncError(), LIVE_SYNC_STALE_MS)
  },
)

watch(() => store.viewMode, (mode) => announce(messages.value.announcement.viewChanged(mode)))

watch(
  () => route.query[MAP_QUERY_KEY],
  () => {
    const routeMapOpen = mapRouteIsOpen()
    if (mapDrawerOpen.value !== routeMapOpen) mapDrawerOpen.value = routeMapOpen
  },
  { immediate: true },
)

watch(
  mapDrawerOpen,
  (isOpen) => {
    syncMapRoute(isOpen)
    if (isOpen && store.activeRaidId !== null) void store.fetchRaidMapPlan()
  },
  { immediate: true },
)

watch(
  () => store.activeRaidId,
  (raidId) => {
    if (raidId !== null && mapDrawerOpen.value) void store.fetchRaidMapPlan()
  },
)

function mapRouteIsOpen(): boolean {
  const value = route.query[MAP_QUERY_KEY]
  return value === MAP_QUERY_VALUE || (Array.isArray(value) && value.includes(MAP_QUERY_VALUE))
}

function syncMapRoute(isOpen: boolean): void {
  if (mapRouteIsOpen() === isOpen) return
  const query = { ...route.query }
  if (isOpen) query[MAP_QUERY_KEY] = MAP_QUERY_VALUE
  else delete query[MAP_QUERY_KEY]
  void router.replace({ query })
}

function onOpenMap(): void {
  if (store.activeRaidId !== null) mapDrawerOpen.value = true
}

function onOpenRaidAudit(): void {
  if (!store.isOfficer || store.activeRaidId === null) return
  void router.push({
    name: FEATURE_ROUTE_NAMES.AuditLog,
    params: {
      guildId: store.activeDiscordGuildId,
      raidId: store.activeRaidId,
    },
  })
}

function onEditMapGroup(groupStableId: string): void {
  if (store.activeRaidEditLocked || (!store.isOwner && !store.isOfficer)) return
  openGroupPlayerSelector(groupStableId)
}

function onEditMapTag(tagId: string): void {
  if (store.activeRaidEditLocked || (!store.isOwner && !store.isOfficer)) return
  openTagPlayerSelector(tagId)
}

function onToggleRaidPublication(): void {
  const raid = activeRaid.value
  if (!store.isOfficer || store.activeRaidEditLocked || raid === null) return
  void store.setRaidPublished(raid.id, !raid.isPublished)
}

function onCloneAnotherRaid(): void {
  void router.push({
    name: APP_ROUTE_NAMES.Calendar,
    params: { guildId: store.activeDiscordGuildId },
    query: { clone: '1' },
  })
}

function onSelectPlannedRaid(raidId: RaidId): void {
  if (raidId === store.activeRaidId) return
  void router.push({
    name: FEATURE_ROUTE_NAMES.GvgPlanner,
    params: { guildId: store.activeDiscordGuildId, raidId },
  })
}

function onSelectStage(stageId: RaidStageId | null): void {
  store.selectStage(stageId)
  announce(stageId === null
    ? messages.value.announcement.allStages
    : messages.value.announcement.stageSelected(stageId))
}

function onStageReset(scope: 'current' | 'all'): void {
  announce(scope === 'all'
    ? messages.value.announcement.allStageChangesReset
    : messages.value.announcement.stageChangesReset)
}

/**
 * Display labels for the four game-stage kinds. Mirrors the helper in
 * `StageChipStrip.vue` so live-region announcements use the same
 * in-game vocabulary (forest / towers / goose / tree) the chip strip renders.
 * `Opening` and `Custom` keep their existing names for consistency.
 */
function stageKindLabel(kind: RaidStageKind): string {
  return kind === RaidStageKind.Forest
    ? messages.value.stageKind.forest
    : kind === RaidStageKind.MidPush
    ? messages.value.stageKind.towers
    : kind === RaidStageKind.LateDefense
      ? messages.value.stageKind.goose
      : kind === RaidStageKind.Endgame
        ? messages.value.stageKind.tree
        : kind === RaidStageKind.Custom
          ? messages.value.stageKind.custom
          : messages.value.stageKind.opening
}

async function onAddStage(): Promise<void> {
  if (store.activeRaidEditLocked) return
  // Auto-seeded defaults are the four game-stage rows (mid_push / late_defense /
  // forest / endgame = Towers / Goose / Forest / Tree). `Opening` is
  // not added by default; officers who want one can still request it via the
  // store's `addStage({ kind })` API directly. `Custom` is the catch-all for
  // the 6th+ stage.
  const present = new Set(store.raidStages.map((stage) => stage.kind))
  const order: ReadonlyArray<RaidStageKind> = [RaidStageKind.MidPush, RaidStageKind.LateDefense, RaidStageKind.Forest, RaidStageKind.Endgame, RaidStageKind.Custom]
  const kind = order.find((candidate) => !present.has(candidate)) ?? RaidStageKind.Custom
  if (await store.addStage({ kind })) announce(messages.value.announcement.stageAdded(stageKindLabel(kind)))
}

async function onRemoveActiveStage(): Promise<void> {
  if (store.activeRaidEditLocked) return
  const stage = store.raidStages.find((candidate) => candidate.id === store.activeStageId)
  if (stage !== undefined) await store.removeStage(stage.id, stage.name || stageKindLabel(stage.kind))
}

function onTagFilterClick(tagId: string): void {
  store.setActiveTagFilter(store.isActiveTagFilter(tagId) ? null : tagId)
}

function onKeydown(event: KeyboardEvent): void {
  const target = event.target as HTMLElement | null
  if (target !== null && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return
  if (event.key === 'Escape') {
    pickerController.value?.closePicker()
    return
  }
  if (
    event.metaKey
    || event.ctrlKey
    || event.altKey
    || !store.isOfficer
    || store.activeRaidEditLocked
  ) return
  if ((event.key === 's' || event.key === 'S') && !pickerController.value?.isOpen) {
    event.preventDefault()
    store.sortAllGroups()
    announce(messages.value.announcement.sortedByRole)
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  void store.fetchBoard()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>
